import React from "react";
import { T } from "../../app/tokens.js";
import { LEVERS, formatLever, isScenarioActive } from "./capacityModel.js";

function Stepper({ lever, value, onChange }) {
  const clamp = (v) => Math.min(lever.max, Math.max(lever.min, Math.round(v * 10) / 10));
  const changed = Math.abs(value - lever.neutral) > 1e-9;
  const btn = { width: 26, height: 28, border: "none", background: T.lightGray, color: T.mid, fontSize: T.fsBase, cursor: "pointer", fontFamily: "inherit" };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
      <span style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark }}>{lever.label}</span>
      <div style={{ display: "inline-flex", alignItems: "center", border: `1px solid ${changed ? T.blueSoftBorder : T.border}`, borderRadius: 7, overflow: "hidden", flexShrink: 0 }}>
        <button type="button" aria-label={`Decrease ${lever.label}`} data-testid={`capacity-lever-${lever.key}-dec`} disabled={value <= lever.min} onClick={() => onChange(clamp(value - lever.step))} style={btn}>{"\u2013"}</button>
        <span data-testid={`capacity-lever-${lever.key}-value`} style={{ minWidth: 58, textAlign: "center", fontSize: T.fsSm, fontWeight: T.fwBold, color: changed ? T.blue : T.dark, background: changed ? T.blueSoft : T.card, lineHeight: "28px" }}>{formatLever(lever.key, value)}</span>
        <button type="button" aria-label={`Increase ${lever.label}`} data-testid={`capacity-lever-${lever.key}-inc`} disabled={value >= lever.max} onClick={() => onChange(clamp(value + lever.step))} style={btn}>+</button>
      </div>
    </div>
  );
}

function Chip({ label, value, testId }) {
  return (
    <div data-testid={testId} style={{ border: `1px dashed ${T.borderDark}`, borderRadius: 8, padding: "6px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.4, textTransform: "uppercase", color: T.light }}>{label}</span>
      <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>{value}</span>
    </div>
  );
}

export function CapacityScenarioPanel({ levers, onChange, onReset, onClose, policy, policyError, canEditPolicy, result }) {
  const active = isScenarioActive(levers);
  return (
    <aside data-testid="capacity-scenario-panel" aria-label="Scenario" style={{ width: 256, boxSizing: "border-box", flexShrink: 0, borderLeft: `1px solid ${T.border}`, paddingLeft: 12, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>Scenario</span>
        <button type="button" data-testid="capacity-scenario-close" aria-label="Close scenario panel" onClick={onClose}
          style={{ border: 0, background: "transparent", color: T.mid, cursor: "pointer", fontSize: T.fsLg, fontFamily: "inherit" }}>{"\u00D7"}</button>
      </div>
      {!active && <div data-testid="capacity-scenario-prompt" style={{ fontSize: T.fsSm, color: T.dark, lineHeight: 1.45 }}>Try a scenario: change a control to see which markets and months shift.</div>}
      <div style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.45 }}>
        Growth compounds on demand (projected assigned headcount). Every other control adjusts usable seats relative to today's attendance, with the same effect on usable seats and the attendance-adjusted maximum. All start at no change.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {LEVERS.map((l) => <Stepper key={l.key} lever={l} value={levers[l.key]} onChange={(v) => onChange({ ...levers, [l.key]: v })} />)}
      </div>
      <button type="button" data-testid="capacity-scenario-reset" disabled={!active} onClick={onReset}
        style={{ alignSelf: "flex-start", border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, color: active ? T.dark : T.light, padding: "6px 12px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: active ? "pointer" : "default", fontFamily: "inherit" }}>
        Reset to today
      </button>
      <div>
        <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.light, marginBottom: 6 }}>Policy guardrails</div>
        {policyError
          ? <div data-testid="capacity-policy-error" style={{ fontSize: T.fsXs, color: T.ragRed }}>{policyError}</div>
          : <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip testId="capacity-chip-density" label="Density floor" value={policy?.sfFloor != null ? `${policy.sfFloor} sf / seat` : "\u2014"} />
            <Chip testId="capacity-chip-buffer" label="Target buffer" value={policy?.hcBufferPct != null ? `${policy.hcBufferPct}%` : "\u2014"} />
          </div>}
        {canEditPolicy && <a href="/admin?section=space_policy" data-testid="capacity-edit-policy" style={{ display: "inline-block", marginTop: 8, fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.blue, textDecoration: "none" }}>Edit Policy in Admin {"\u2192"}</a>}
      </div>
      {active && result && (
        <div data-testid="capacity-scenario-result" style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10, fontSize: T.fsSm, fontWeight: T.fwBold, color: result.tone === "strained" ? T.ragRed : T.greenDeep }}>
          {result.text}
        </div>
      )}
    </aside>
  );
}
